import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * One column in a custom report's table, stored in the `columns` JSON of a
 * {@link CustomReportDefinitionEntity}. The `key` must match a column alias the
 * report's SQL selects. Keys for gross-profit values intentionally carry a `gp`
 * token so the global GpStripInterceptor blanks them for users without
 * `orders:gp:view`, exactly like the built-in reports.
 */
export interface CustomReportColumnModel {
	/** Matches a SELECT alias in the report's SQL. */
	key: string;
	/** Header label (run through t() on the frontend). */
	label: string;
	/** Optional column alignment. */
	align?: 'left' | 'center' | 'right';
	/**
	 * Number-formatting config. Loosely typed here as a record to avoid a
	 * shared→core dependency, but interpreted by the frontend renderer as
	 * `FormatOptions` from `@supply-mover-2-0/core` and passed to the shared
	 * `formatNumberWithFormatOptions` (the same formatter the list-builder uses).
	 * Omit for plain text columns (rendered via the translation function).
	 * Example: `{ numberFormat: 'currency', decimalPlaces: 2 }` or
	 * `{ numberFormat: 'percent', decimalPlaces: 4, trimTrailingZeros: true }`.
	 */
	format?: Record<string, unknown>;
}

/** One selectable option for a {@link CustomReportFilterModel} of type `select`. */
export interface CustomReportFilterOptionModel {
	value: string;
	label: string;
}

/**
 * A configurable, report-specific filter — beyond the built-in date-range,
 * company, and salesperson-scope filters every custom report already has.
 *
 * The frontend renders one control per filter; the engine injects a SQL fragment
 * into the report's `sqlTemplate` at the matching `{{FILTER:<key>}}` placeholder,
 * binding the selected value to the named param `:<key>`. When no value is
 * selected (e.g. the "All" option) the placeholder collapses to empty.
 *
 * `sql` is server-only and never sent to the client.
 */
export interface CustomReportFilterModel {
	/** Filter id — also the named SQL param `:<key>` and the `{{FILTER:<key>}}` token. */
	key: string;
	/** Control label (run through t() on the frontend). */
	label: string;
	/** Control type. Only `select` is supported today. */
	type: 'select';
	/**
	 * Dynamic option source the frontend knows how to populate. `rbacRoles` lists
	 * the tenant's RBAC roles (value = role name). Mutually exclusive with
	 * `options`.
	 */
	optionsSource?: 'rbacRoles';
	/** Static options (alternative to `optionsSource`). */
	options?: Array<CustomReportFilterOptionModel>;
	/** Default selected value applied when the request doesn't specify one. */
	defaultValue?: string | null;
	/** When true, offer an "All" choice (empty value) that disables the filter. */
	includeAllOption?: boolean;
	/** Label for the "All" choice (defaults to "All"). */
	allOptionLabel?: string;
	/**
	 * SQL fragment injected at `{{FILTER:<key>}}` when a value is selected. Must
	 * reference the selected value via the named param `:<key>` (bound safely).
	 * Authored by the dev team; never user input. SERVER-ONLY — stripped from the
	 * client response.
	 */
	sql: string;
}

/**
 * The client-safe shape of a filter (everything except the server-only `sql`),
 * returned to the frontend so it can render the control.
 */
export type CustomReportFilterClientModel = Omit<
	CustomReportFilterModel,
	'sql'
>;

/**
 * A tenant-specific dynamic report definition.
 *
 * Each row fully describes one custom report: its registry metadata, the column
 * spec the generic frontend renders, and a read-only SQL template the backend
 * engine executes. The template contains three placeholders the engine fills in
 * before running, so every custom report gets the same date-range, company, and
 * salesperson data-scope enforcement as the built-in reports:
 *  - `{{DATE_RANGE}}`        → `<dateColumn> >= :startDate AND <dateColumn> <= :endDate`
 *  - `{{COMPANY_FILTER}}`    → ` AND <companyColumn> IN (:validCompanyIds)` (or empty)
 *  - `{{SALESPERSON_SCOPE}}` → the reporting:data:* owner-scope fragment for `clientAlias`
 *
 * Definitions are authored by the dev team (never user input) and seeded
 * per-tenant, which is what makes a report tenant-specific without shipping
 * per-report code to every tenant.
 */
@Index('idx_custom_report_active', ['isActive'], {})
@Index('idx_custom_report_category', ['category'], {})
@Index('reportKey', ['reportKey'], { unique: true })
@Entity('custom_report_definition')
export class CustomReportDefinitionEntity extends BaseEntity {
	@Column('varchar', { name: 'reportKey', unique: true, length: 100 })
	public reportKey!: string;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: 'category', length: 50 })
	public category!: string;

	/**
	 * RBAC action string checked (exact match) against the caller's permission
	 * set to gate this report. Stored as a plain string (not a shared RbacActions
	 * constant) because tenant-specific report permissions live only in the
	 * tenant DB, not in shipped code.
	 *
	 * CONVENTION: use `reporting:custom:view:<reportKey>`. Two things rely on this
	 * shape: (1) RbacService.getActions surfaces it so it's grantable in the RBAC
	 * UI; (2) the CRM nav shows the "Custom Reports" section via
	 * PermissionService.canAnyMatching('reporting:custom:view:'), so any action
	 * under that prefix reveals the section.
	 */
	@Column('varchar', { name: 'permissionAction', length: 150 })
	public permissionAction!: string;

	/** Column expression for the {{DATE_RANGE}} placeholder, e.g. `inv.date`. */
	@Column('varchar', { name: 'dateColumn', length: 100 })
	public dateColumn!: string;

	/**
	 * Column expression for the {{COMPANY_FILTER}} placeholder, e.g.
	 * `inv.fk_company__id`. Null disables company filtering for this report.
	 */
	@Column('varchar', { name: 'companyColumn', nullable: true, length: 100 })
	public companyColumn!: string | null;

	/**
	 * Client table alias the {{SALESPERSON_SCOPE}} fragment scopes on, e.g. `c`.
	 * Null disables salesperson data-scope (report shows all rows to permitted
	 * users). Ignored when `scopePrimaryColumn` is set.
	 */
	@Column('varchar', { name: 'clientAlias', nullable: true, length: 32 })
	public clientAlias!: string | null;

	/**
	 * Optional owner-id column EXPRESSION for the {{SALESPERSON_SCOPE}}
	 * fragment's PRIMARY dimension, e.g.
	 * `COALESCE(o._fk_user__id, c._fk_user__id)`. When set it takes precedence
	 * over `clientAlias`, so a report whose displayed salesperson is NOT simply
	 * the client owner (e.g. the order-level salesperson with a client-owner
	 * fallback — PD-14039) filters on the same expression it displays. The
	 * "Unassigned" dimension tests this expression IS NULL.
	 */
	@Column('varchar', {
		name: 'scopePrimaryColumn',
		nullable: true,
		length: 255,
	})
	public scopePrimaryColumn!: string | null;

	/**
	 * Optional owner-id column expression for the ALTERNATE dimension when
	 * `scopePrimaryColumn` is set, e.g. `c._fk_user__id_alternate`. Null folds
	 * alternate-scope user ids into the primary match (see
	 * buildOwnerScopeSqlFragment).
	 */
	@Column('varchar', {
		name: 'scopeAlternateColumn',
		nullable: true,
		length: 255,
	})
	public scopeAlternateColumn!: string | null;

	@Column('json', { name: 'columns' })
	public columns!: Array<CustomReportColumnModel>;

	/**
	 * Optional report-specific filters (see {@link CustomReportFilterModel}). Each
	 * declares a `{{FILTER:<key>}}` placeholder the engine fills in the SQL. Null /
	 * empty means the report has only the built-in date/company/scope filters.
	 */
	@Column('json', { name: 'filters', nullable: true })
	public filters!: Array<CustomReportFilterModel> | null;

	/** The read-only SELECT template. Named `sqlTemplate` to avoid the SQL token. */
	@Column('text', { name: 'sqlTemplate' })
	public sqlTemplate!: string;

	@Column('tinyint', {
		name: 'isActive',
		nullable: true,
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean | null;

	@Column('varchar', {
		name: 'surface',
		length: 16,
		default: () => "'CRM'",
	})
	public surface!: 'CRM' | 'STOREFRONT';
}
